import { useMemo, type JSX } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CalendarRange, Users } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { Combobox } from '@/components/ui/combobox';
import { useAdminTeams } from '@/hooks/useAdmin';
import { useMainScopeLabel } from '@/hooks/useTeamScope';
import { useUrlState } from '@/hooks/useUrlState';
import { formatDayRange, INSIGHTS_PRESETS, legacyInsightsTarget, type InsightsPreset } from '@/lib/insights';
import { DEFAULT_EXCLUDED_LINKS, teamSection } from '@/lib/teams';
import { cn } from '@/lib/utils';
import { InsightsScopeProvider, useInsightsScope } from './scope';
import { ScorecardTab } from './ScorecardTab';
import { FunnelTab } from './FunnelTab';
import { OutreachersTab } from './OutreachersTab';
import { SpendTab } from './SpendTab';
import { SourcesTab } from './SourcesTab';
import { QualityTab } from './QualityTab';

/**
 * Insights: one place for reporting, replacing Performance, Funnel & ROI and
 * Niche performance. Every tab shares one scope (period + team, with the
 * period before for comparison) and the backend's metric definitions, so
 * the same number means the same thing on every tab.
 */
export const INSIGHTS_TABS = [
  { id: 'scorecard', label: 'Scorecard', element: <ScorecardTab /> },
  { id: 'funnel', label: 'Funnel', element: <FunnelTab /> },
  { id: 'outreachers', label: 'Outreachers', element: <OutreachersTab /> },
  { id: 'spend', label: 'Spend', element: <SpendTab /> },
  { id: 'sources', label: 'Sources', element: <SourcesTab /> },
  { id: 'quality', label: 'Quality', element: <QualityTab /> },
] as const;

export type InsightsTabId = (typeof INSIGHTS_TABS)[number]['id'];

export function AdminInsights(): JSX.Element {
  return (
    <InsightsScopeProvider>
      <InsightsPage />
    </InsightsScopeProvider>
  );
}

function InsightsPage(): JSX.Element {
  const [{ tab }, setUrl] = useUrlState({ tab: 'scorecard' });
  const active = INSIGHTS_TABS.find((t) => t.id === tab) ?? INSIGHTS_TABS[0];
  const scope = useInsightsScope();
  const section = scope.locked ? teamSection(scope.team) : undefined;

  return (
    <div className="space-y-5">
      <PageHeader
        title={section ? `${section.label} insights` : 'Insights'}
        description="How outreach is doing, from the first message to a posted clip, and what it cost. Hover a metric for its definition; counts open the invites behind them."
      >
        <ScopeBar />
      </PageHeader>

      <div role="tablist" aria-label="Insights" className="-mx-1 flex gap-1 overflow-x-auto border-b border-border px-1">
        {INSIGHTS_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`insights-tab-${t.id}`}
            aria-selected={t.id === active.id}
            aria-controls={`insights-panel-${t.id}`}
            onClick={() => setUrl({ tab: t.id })}
            className={cn(
              '-mb-px shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-[120ms]',
              t.id === active.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`insights-panel-${active.id}`} aria-labelledby={`insights-tab-${active.id}`}>
        {active.element}
      </div>
    </div>
  );
}

/** Period presets (or a custom range) and the team, plus a line saying what's compared with what. */
function ScopeBar(): JSX.Element {
  const scope = useInsightsScope();
  const teams = useAdminTeams();
  const mainLabel = useMainScopeLabel();
  const teamOptions = useMemo(
    () => (teams.data ?? []).filter((t) => t.isActive || t.code === scope.team).map((t) => ({ value: t.code, label: t.displayName })),
    [teams.data, scope.team],
  );
  const options: { key: InsightsPreset; label: string }[] = [...INSIGHTS_PRESETS, { key: 'custom', label: 'Custom' }];
  const { current, previous, problem } = scope.windows;

  return (
    <div className="panel space-y-3 rounded-xl p-3 sm:p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div
          role="group"
          aria-label="Period"
          className="inline-flex flex-wrap items-center rounded-lg border border-input bg-card p-0.5"
        >
          {options.map((p) => (
            <button
              key={p.key}
              type="button"
              aria-pressed={scope.preset === p.key}
              onClick={() => scope.setPreset(p.key)}
              className={cn(
                'inline-flex h-8 items-center rounded-md px-2.5 text-sm font-medium transition-colors duration-[120ms] pointer-coarse:px-3.5',
                scope.preset === p.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        {scope.preset === 'custom' && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <label className="inline-flex items-center gap-1.5 text-muted-foreground">
              From
              <input
                type="date"
                value={scope.custom.from}
                onChange={(e) => scope.setCustom({ from: e.target.value, to: scope.custom.to })}
                className="h-9 rounded-lg border border-input bg-transparent px-2 text-sm text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              />
            </label>
            <label className="inline-flex items-center gap-1.5 text-muted-foreground">
              To
              <input
                type="date"
                value={scope.custom.to}
                onChange={(e) => scope.setCustom({ from: scope.custom.from, to: e.target.value })}
                className="h-9 rounded-lg border border-input bg-transparent px-2 text-sm text-foreground focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              />
            </label>
          </div>
        )}
        {!scope.locked && (
          <div className="w-full sm:ml-auto sm:w-64">
            <Combobox
              aria-label="Team"
              value={scope.team}
              onChange={scope.setTeam}
              options={teamOptions}
              allowAll
              allLabel={`${mainLabel} (default)`}
              placeholder="Team"
              loading={teams.isLoading}
              className="w-full"
            />
          </div>
        )}
      </div>
      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-start gap-1.5">
          <CalendarRange className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            <span className="font-medium text-foreground">{formatDayRange(current)}</span> (UTC days), compared with{' '}
            {formatDayRange(previous)}
          </span>
        </span>
        <span className="inline-flex items-start gap-1.5">
          <Users className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            <span className="font-medium text-foreground">{scope.scopeLabel}</span>
            {!scope.team && !scope.locked
              ? ` · ${DEFAULT_EXCLUDED_LINKS.map((s) => s.label).join(' and ')} are left out`
              : null}
          </span>
        </span>
      </p>
      {problem ? (
        <p role="status" className="text-xs text-pending-strong">
          {problem}
        </p>
      ) : null}
    </div>
  );
}

/** The old reporting pages' routes land on the matching tab (see legacyInsightsTarget). */
export function LegacyInsightsRedirect({ page }: { page: 'inviters' | 'niches' | 'efficiency' }): JSX.Element {
  const location = useLocation();
  return <Navigate to={legacyInsightsTarget(page, location.search)} replace />;
}
